import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { num1 } from '../../lib/numeros';
import NombreConEtiqueta from './NombreConEtiqueta';

/**
 * LAS SUGERENCIAS DEL INTRA Y DEL POST (Jesús, artefacto «El sugeridor del perientreno», 25-09).
 *
 * Tres bloques en el orden del método (lo decide el servidor, core/peri_sugerencias), tres de
 * cada uno y «Ver todos», y UNA sola cantidad por fila: la que cubre el 100 % de lo que falta.
 *
 * EN EL INTRA, TRES PASOS (artefacto «El intra, paso a paso», 27-09): «los tres grupos ya están
 * y ya están en el orden bueno. Lo que no está es que alguien le diga que son tres pasos y no
 * una lista». Misma hoja y mismos alimentos, con un título delante de cada grupo:
 *   1. «En primer lugar, aminoácidos en forma libre» (con la línea de las sugerencias);
 *   2. «Segundo, hidratos de asimilación rápida» + «Puedes elegir uno o combinar varios»;
 *   3. «Para terminar, puedes añadir isotónico sin azúcar al gusto» + lo del agua.
 * Van seguidos en la misma pantalla («se baja, no se pasa de página»): el paso hecho se recoge
 * en una línea con lo elegido y el siguiente se abre. En el paso 3 no pone «No aporta macros»
 * sino «Al gusto». Los líquidos, en ml (`es_liquido`, del servidor).
 */
const TITULOS = { proteina: 'Proteína', hidratos: 'Hidratos', sin_macros: 'Sin macros' };

const PASOS = {
    intra: [
        { bloque: 'proteina', titulo: 'En primer lugar, aminoácidos en forma libre',
          corto: 'En primer lugar, aminoácidos en forma libre', hecho: 'Aminoácidos en forma libre' },
        { bloque: 'hidratos', titulo: 'Segundo, hidratos de asimilación rápida',
          corto: 'Segundo, hidratos de asimilación rápida', hecho: 'Hidratos de asimilación rápida',
          sub: 'Puedes elegir uno o combinar varios. Ej: ciclodextrina + Gatorade.' },
        // SIN «AL GUSTO» (Jesús, audio reenviado por Gonzalo el 28-09: «lo de al gusto,
        // eso quítalo, eso sobra»): la cantidad ya va en el botón, 500 ml.
        { bloque: 'sin_macros', titulo: 'Para terminar, puedes añadir isotónico sin azúcar',
          corto: 'Para terminar, isotónico sin azúcar', hecho: 'Isotónico sin azúcar',
          sub: 'O eso, o agua. Pero bebe mínimo 500-700 ml por cada hora de ejercicio.', opcional: true },
    ],
    // EL POST (artefacto «El postentreno, cerrado», 27-09): «Crea tu postentreno en tres pasos».
    post: [
        // Con su punto, «el texto, tal cual lo dijiste» (sección 04).
        { bloque: 'proteina', titulo: 'Añade la proteína.', corto: 'Añade la proteína.', hecho: 'La proteína' },
        { bloque: 'hidratos', titulo: 'Ahora el hidrato.', corto: 'Ahora el hidrato.', hecho: 'El hidrato' },
        // Jesús, 27-09 22:49: el título hasta «sin azúcares añadidos»; lo de la grasa, debajo y en
        // pequeño, «con el mismo tamaño que "te quedan 4 gramos de grasa"», y el 4 en número.
        { bloque: 'bebida', titulo: 'Puedes dejarlo aquí o completar tu postentreno con una bebida vegetal sin azúcares añadidos.',
          sub: 'Puedes llegar hasta los 4 gramos de grasa como máximo.',
          corto: 'Puedes dejarlo aquí o completar con una bebida vegetal', hecho: 'La bebida vegetal', opcional: true, grasa: true },
    ],
};
const CABECERA = { post: 'Crea tu postentreno en tres pasos' };
export const TOPE_GRASA_POST = 4;

const unidad = (f) => (f?.es_liquido ? 'ml' : 'g');

function lineaDeMacros(m, alGusto = false) {
    const partes = [];
    if ((m?.P || 0) > 0) partes.push(`P=${num1(m.P)} g`);
    if ((m?.H || 0) > 0) partes.push(`H=${num1(m.H)} g`);
    if ((m?.G || 0) > 0) partes.push(`G=${num1(m.G)} g`);
    return partes.length ? partes.join(' ') : (alGusto ? 'Al gusto' : 'No aporta macros');
}

function Fila({ f, destacado, alGusto, onAdd }) {
    return (
        <div className="flex items-center gap-2 py-2">
            <button type="button" onClick={() => onAdd(f)} className="flex-1 min-w-0 text-left"
                data-testid={`peri-item-${f.id}`}>
                <div className="text-sm text-foreground leading-snug break-words">
                    <NombreConEtiqueta nombre={f.nombre} promocionado={f.is_promocionado} />
                </div>
                <div className="text-xs text-muted-foreground">
                    {/* SIN «AL GUSTO» (Jesús, 27-09: «eso sobra, estamos poniendo 500 ml delante»):
                        en el paso 3 la línea de debajo no lleva nada; la cantidad va en el botón. */}
                    {alGusto ? null : (
                        <>{num1(f._cantidad_sugerida)} {unidad(f)} → <span className="font-semibold text-foreground">{lineaDeMacros(f._macros_sugeridos)}</span></>
                    )}
                </div>
            </button>
            <button type="button" onClick={() => onAdd(f)} data-testid={`peri-add-${f.id}`}
                className={`flex-shrink-0 h-8 px-2.5 rounded-lg text-xs font-bold transition-colors ${destacado
                    ? 'border-2 border-brand-orange text-brand-orange hover:bg-brand-orange/10'
                    : 'border border-border text-muted-foreground hover:border-brand-orange hover:text-brand-orange'}`}>
                {num1(f._cantidad_sugerida)} {unidad(f)}
            </button>
        </div>
    );
}

export default function PeriSugerencias({ api, peri, faltaP, faltaH, onAdd, elegidos = [] }) {
    // La grasa que le queda al post de sus 4 g (el paso 3). En el intra no se usa.
    const grasaPuesta = elegidos.reduce((t, a) => t + (a.macros_efectivos?.G || 0), 0);
    const gRest = Math.round((TOPE_GRASA_POST - grasaPuesta) * 10) / 10;
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [abiertos, setAbiertos] = useState({});
    // El paso que se ha abierto a mano (para volver a uno hecho y combinar); null = el que toca.
    const [pasoAMano, setPasoAMano] = useState(null);

    useEffect(() => {
        let cancelado = false;
        setCargando(true);
        const q = new URLSearchParams({ peri, p_rest: String(Math.max(0, faltaP || 0)),
                                        h_rest: String(Math.max(0, faltaH || 0)),
                                        g_rest: String(Math.max(0, gRest)) });
        api(`/api/calculator/peri-sugerencias?${q}`)
            .then((r) => { if (!cancelado) setDatos(r); })
            .catch(() => { if (!cancelado) setDatos({ bloques: {} }); })
            .finally(() => { if (!cancelado) setCargando(false); });
        return () => { cancelado = true; };
        // `api` fuera a propósito: llega recreada en cada pintado y relanzaría la petición sin fin.
    }, [peri, faltaP, faltaH, gRest]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!datos && cargando) {
        return <div className="text-center py-8 text-muted-foreground">Cargando...</div>;
    }
    const bloques = datos?.bloques || {};
    const visibles = datos?.visibles || 3;
    const orden = ['proteina', 'hidratos', 'sin_macros'].filter((b) => (bloques[b] || []).length > 0);
    if (orden.length === 0) {
        return <div className="text-center py-8 text-muted-foreground">No hay alimentos que sugerir para esta comida.</div>;
    }

    const lista = (b, conFilas) => {
        const todos = bloques[b] || [];
        const abierto = Boolean(abiertos[b]);
        const enseñados = abierto ? todos : todos.slice(0, visibles);
        return (
            <>
                <div className="divide-y divide-border/60">
                    {enseñados.map((f, i) => (
                        <Fila key={`${f.id}-${i}`} f={f} destacado={i === 0 && b !== 'sin_macros'}
                            alGusto={conFilas === 'al_gusto'}
                            onAdd={(x) => { setPasoAMano(null); onAdd(x); }} />
                    ))}
                </div>
                {todos.length > visibles && (
                    <button type="button" onClick={() => setAbiertos((a) => ({ ...a, [b]: !abierto }))}
                        className="mt-1 text-xs font-semibold text-brand" data-testid={`peri-ver-todos-${b}`}>
                        {abierto ? 'Ver menos' : `Ver todos (${todos.length}) ›`}
                    </button>
                )}
            </>
        );
    };

    // ── EN PASOS (el intra) ──
    const pasos = PASOS[peri];
    if (pasos) {
        // A qué bloque pertenece cada alimento ya elegido: por su propia marca (si vino de aquí)
        // o por la lista de sugerencias.
        const deQueBloque = {};
        for (const b of Object.keys(bloques)) for (const f of bloques[b] || []) deQueBloque[f.id] = b;
        const elegidosDe = (b) => elegidos.filter((a) => (a.bloque_peri || deQueBloque[a.alimento_id ?? a.id]) === b);
        const hechos = pasos.map((p) => elegidosDe(p.bloque).length > 0);
        const queToca = hechos.findIndex((h, i) => !h && !pasos[i].opcional);
        // Hechos los obligatorios, se abre el siguiente opcional que siga vacío; si también está
        // hecho, se recoge como los demás (Jesús, 27-09 22:25: «la persona pincha agua y ya
        // acabaría el tercer paso»). Antes el último quedaba siempre abierto.
        const siguienteVacio = hechos.findIndex((h) => !h);
        const abiertoAhora = pasoAMano ?? (queToca === -1 ? siguienteVacio : queToca);

        return (
            <div className="space-y-3" data-testid={`peri-sugerencias-${peri}`}>
                {CABECERA[peri] && (
                    <p className="font-heading text-base font-bold text-foreground" data-testid="peri-cabecera">{CABECERA[peri]}</p>
                )}
                {pasos.map((p, i) => {
                    if (!(bloques[p.bloque] || []).length) return null;
                    const hecho = hechos[i];
                    const abierto = i === abiertoAhora;
                    if (hecho && !abierto) {
                        return (
                            <button key={p.bloque} type="button" onClick={() => setPasoAMano(i)}
                                data-testid={`peri-paso-${i + 1}-hecho`}
                                className="w-full flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5 text-left">
                                <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3.5 h-3.5" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold text-foreground">{p.hecho}</span>
                                    {elegidosDe(p.bloque).map((a, j) => (
                                        <span key={j} className="block text-xs text-muted-foreground">
                                            {a.nombre} · {num1(a.cantidad_g)} {unidad(a)}
                                            {(a.macros_efectivos?.P || 0) > 0 && ` · P=${num1(a.macros_efectivos.P)}`}
                                            {(a.macros_efectivos?.H || 0) > 0 && ` · H=${num1(a.macros_efectivos.H)}`}
                                        </span>
                                    ))}
                                </span>
                            </button>
                        );
                    }
                    return (
                        <section key={p.bloque} data-testid={`peri-paso-${i + 1}`}
                            className={abierto ? '' : 'opacity-60'}>
                            <button type="button" onClick={() => setPasoAMano(i)} className="w-full flex items-start gap-3 text-left">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${abierto
                                    ? 'bg-brand text-white' : 'border border-border text-muted-foreground'}`}>{i + 1}</span>
                                <span className="min-w-0">
                                    <span className={`block text-sm font-bold leading-snug ${abierto ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {abierto ? p.titulo : p.corto}
                                    </span>
                                    {abierto && p.sub && <span className={`block text-xs mt-0.5 ${p.grasa ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{p.sub}</span>}
                                    {abierto && p.grasa && (
                                        <span className="block text-xs font-semibold text-foreground mt-0.5" data-testid="grasa-que-queda">
                                            {gRest > 0 ? `Te quedan ${num1(gRest)} g de grasa.` : 'Ya estás en los 4 g de grasa.'}
                                        </span>
                                    )}
                                    {abierto && i === 0 && (
                                        <span className="block text-[11px] text-muted-foreground mt-0.5" data-testid="origen-lista">
                                            Sugerencias para lo que le falta a esta comida, con la cantidad ya ajustada
                                        </span>
                                    )}
                                </span>
                            </button>
                            {abierto && <div className="mt-1 pl-9">{p.grasa && gRest <= 0 ? null : lista(p.bloque, p.bloque === 'sin_macros' ? 'al_gusto' : null)}</div>}
                        </section>
                    );
                })}
            </div>
        );
    }

    // ── EN BLOQUES (el post, de momento) ──
    return (
        <div className="space-y-4" data-testid={`peri-sugerencias-${peri}`}>
            <p className="text-[11px] text-muted-foreground" data-testid="origen-lista">
                Sugerencias para lo que le falta a esta comida, con la cantidad ya ajustada
            </p>
            {orden.map((b) => (
                <section key={b} data-testid={`peri-bloque-${b}`}>
                    <h3 className={`text-[11px] font-bold uppercase tracking-widest mb-1 ${b === 'sin_macros' ? 'text-muted-foreground' : 'text-brand'}`}>
                        {TITULOS[b]}
                    </h3>
                    {lista(b)}
                </section>
            ))}
        </div>
    );
}
